/**
 * 时区工具。
 *
 * 工地在洛杉矶（America/Los_Angeles），有夏令时。所有「按天」的计算
 * ——工时归日、今日打卡、结算周期边界——都必须按工地时区算，
 * 用 UTC 会让下午的打卡跑到第二天去。
 *
 * 只用标准库 Intl，不引 date-fns-tz：需要的就这三个函数。
 */
const FORMATTER_CACHE = new Map();
function formatter(timeZone) {
    let cached = FORMATTER_CACHE.get(timeZone);
    if (!cached) {
        // 构造 Intl.DateTimeFormat 不便宜，而工时计算会对每条记录调一次
        cached = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
        FORMATTER_CACHE.set(timeZone, cached);
    }
    return cached;
}
function wallClock(at, timeZone) {
    const parts = formatter(timeZone).formatToParts(at);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const hour = get('hour');
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        // 某些环境下午夜会被格式化成 24 而不是 0
        hour: hour === 24 ? 0 : hour,
        minute: get('minute'),
        second: get('second'),
    };
}
/** 取某个时刻在指定时区下的自然日，格式 YYYY-MM-DD */
export function dayKeyInTimeZone(at, timeZone) {
    const w = wallClock(at, timeZone);
    return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}
/**
 * 指定时区在某一时刻的 UTC 偏移（毫秒，东为正）。
 * 做法是把当地的时钟读数当成 UTC 来算，差值就是偏移量。
 */
export function timeZoneOffsetMs(at, timeZone) {
    const w = wallClock(at, timeZone);
    const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
    // 抹掉毫秒差：wallClock 精度到秒
    return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}
/**
 * 求「该时区某个日历日的零点」对应的 UTC 时刻。
 *
 * 要解 T 使得 local(T) = Y-M-D 00:00。因为 offset 本身依赖 T
 * （夏令时切换日两侧不同），用两次迭代收敛。
 */
function utcForLocalMidnight(year, month, day, timeZone) {
    const wanted = Date.UTC(year, month - 1, day);
    let t = wanted;
    for (let i = 0; i < 2; i++) {
        t = wanted - timeZoneOffsetMs(new Date(t), timeZone);
    }
    return new Date(t);
}
/**
 * 某个时刻所在自然日（按工地时区）的起止，返回 UTC 时间点。
 * 左闭右开：`start <= t < end`。
 *
 * 结束时间是**次日零点**而不是 `start + 24h` —— 夏令时切换那两天
 * 一天只有 23 小时或有 25 小时，加 24 小时会漏掉或多算一个小时的打卡。
 */
export function dayBoundsInTimeZone(at, timeZone) {
    const w = wallClock(at, timeZone);
    return {
        start: utcForLocalMidnight(w.year, w.month, w.day, timeZone),
        // Date.UTC 会自动处理跨月跨年（day + 1 超出当月天数时）
        end: utcForLocalMidnight(w.year, w.month, w.day + 1, timeZone),
    };
}
/** 从 YYYY-MM-DD 求该自然日在指定时区的起止 UTC 时刻 */
export function dayBoundsFromKey(dayKey, timeZone) {
    const [year, month, day] = dayKey.split('-').map(Number);
    return {
        start: utcForLocalMidnight(year ?? 1970, month ?? 1, day ?? 1, timeZone),
        end: utcForLocalMidnight(year ?? 1970, month ?? 1, (day ?? 1) + 1, timeZone),
    };
}
//# sourceMappingURL=timezone.js.map